import React, { useState, useEffect } from 'react';
import LoginPage from './components/LoginPage';
import api from './services/api';
import {
  LogOut, FileText, Users, Calendar, BarChart3, BookOpen,
  GraduationCap, Link, UserCheck, Plus, Edit, Trash2, Search,
  Upload, Download, X, CheckCircle2, ChevronDown, ChevronUp,
  Shield, Heart, CheckSquare, Users2, Lightbulb, Clock,
  KeyRound, UserCog, School, BookMarked, Tag, AlertCircle, TrendingUp, History, ClipboardList
} from 'lucide-react';
import * as XLSX from 'xlsx';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import MahasiswaRiwayatPage from './components/MahasiswaRiwayatPage';
import MahasiswaProfilPage from './components/MahasiswaProfilPage';
import NotificationBell from './components/NotificationBell';
import AuditLogPage from './components/AuditLogPage';
import './index.css';

// ============================================
// UTILITY FUNCTIONS
// ============================================
const getLevel = (percentage) => {
  const pct = parseFloat(percentage);
  if (pct < 55) return { level: 1, label: 'Level 1', desc: 'Perlu Peningkatan' };
  if (pct < 65) return { level: 2, label: 'Level 2', desc: 'Cukup Baik' };
  if (pct < 75) return { level: 3, label: 'Level 3', desc: 'Baik' };
  if (pct < 85) return { level: 4, label: 'Level 4', desc: 'Sangat Baik' };
  return { level: 5, label: 'Level 5', desc: 'Excellent' };
};

// Form data
const formDataKelas = {
  integrity: { title: 'Integrity', subtitle: 'Integritas & Kejujuran', Icon: Shield, color: '#2D5B8C', maxScore: 44,
    items: ['Menjunjung tinggi nama almamater','Menaati peraturan dan tata tertib','Bertanggungjawab dalam setiap perbuatan','Bersikap jujur dalam perkataan dan perbuatan','Tidak melakukan dan menolak gratifikasi','Percaya diri dan dapat mempercayai orang lain','Disiplin dalam sikap dan perbuatan','Berani menyuarakan kebenaran','Menciptakan suasana kelas yang kondusif','Sederhana dan rendah hati','Mengutamakan kepentingan bersama'] },
  compassion: { title: 'Compassion', subtitle: 'Empati & Kepedulian', Icon: Heart, color: '#A63A3A', maxScore: 28,
    items: ['Bersikap empati terhadap orang lain','Peka dan peduli terhadap kebutuhan orang lain','Murah hati untuk berbagi ide dan ilmu','Ramah dan memberi sapaan tulus','Memberikan pelayanan kepada yang membutuhkan','Saling membantu mengembangkan diri','Sabar dan berani memaafkan'] },
  assurance: { title: 'Assurance', subtitle: 'Jaminan Kualitas', Icon: CheckSquare, color: '#2F6E45', maxScore: 32,
    items: ['Mempersiapkan pembelajaran','Semangat dalam belajar','Berpartisipasi dalam tugas kelompok','Menjamin keaktifan dalam proses pembelajaran','Menjamin terlaksanakannya tugas dengan baik','Menjamin kemampuan bekerja sama','Melakukan tugas dan peran dengan baik','Memelihara situasi kelas tetap bersih dan nyaman'] },
  respect: { title: 'Respect', subtitle: 'Menghargai & Menghormati', Icon: Users2, color: '#5E4B8B', maxScore: 28,
    items: ['Menghormati tanpa membedakan suku, agama, ras','Menghargai keunikan masing-masing pribadi','Mencintai dan menghargai diri sendiri','Menyampaikan pendapat secara santun','Menyediakan waktu untuk orang lain','Berpikir positif terhadap orang lain','Menghargai privasi orang lain'] },
  innovation: { title: 'Embrace Innovation', subtitle: 'Inovasi & Kreativitas', Icon: Lightbulb, color: '#A86A12', maxScore: 28,
    items: ['Menggali minat dan bakat dalam diri','Terus memperbarui dan mengembangkan diri','Tangguh dan mencari solusi kreatif','Terbuka dan selektif terhadap teknologi','Mampu menerima ide dan perubahan konstruktif','Mengungkapkan ide-ide kreatif dan inovatif','Tangguh menghadapi perubahan teknologi'] }
};

const formDataPraktik = {
  integrity: { title: 'Integrity', subtitle: 'Integritas & Kejujuran', Icon: Shield, color: '#2D5B8C', maxScore: 40,
    items: ['Menjunjung tinggi nama almamater','Mentaati peraturan dan tata tertib','Bertanggungjawab dalam setiap perbuatan','Bersikap jujur dalam perkataan dan perbuatan','Tidak melakukan dan menolak gratifikasi','Bersikap professional dalam tugas','Memberikan informasi yang benar dan akurat','Percaya dan mempercayai orang lain','Menjunjung tinggi nilai kesusilaan','Disiplin dalam tugas dan tanggung jawab'] },
  compassion: { title: 'Compassion', subtitle: 'Empati & Kepedulian', Icon: Heart, color: '#A63A3A', maxScore: 24,
    items: ['Peka dan peduli terhadap situasi dan kebutuhan klien','Berempati terhadap teman dan klien','Memberikan bantuan dengan tulus','Melayani dengan sepenuh hati','Sabar, ramah dan penuh pengertian','Melayani dengan terbuka dan gembira'] },
  assurance: { title: 'Assurance', subtitle: 'Jaminan Kualitas', Icon: CheckSquare, color: '#2F6E45', maxScore: 32,
    items: ['Memberikan pelayanan komprehensif dan holistik','Melaksanakan praktik sesuai etika profesi','Menjamin dan menjaga hak serta kerahasiaan klien','Melayani dengan segera klien yang membutuhkan','Melaksanakan praktik sesuai SOP','Menggunakan alat sesuai pedoman','Melakukan tindakan dengan yakin','Mengutamakan patient safety'] },
  respect: { title: 'Respect', subtitle: 'Menghargai & Menghormati', Icon: Users2, color: '#5E4B8B', maxScore: 28,
    items: ['Menghargai privasi klien dan keluarga','Menghormati pasien tanpa membedakan','Menyampaikan pendapat secara santun','Mencintai dan menghargai diri sendiri','Berpikir positif terhadap orang lain','Menghargai bantuan dan pekerjaan orang lain','Menghargai perbedaan dan keunikan orang lain'] },
  innovation: { title: 'Embrace Innovation', subtitle: 'Inovasi & Kreativitas', Icon: Lightbulb, color: '#A86A12', maxScore: 28,
    items: ['Mencari ide baru untuk mengembangkan pelayanan','Belajar dari pengalaman dan mengembangkan diri','Mampu bersikap selektif terhadap perubahan','Mampu menerima perubahan konstruktif','Memberikan pelayanan kreatif sesuai kebutuhan','Menerapkan pelayanan prima','Tangguh menghadapi perubahan teknologi'] }
};

// ============================================
// TOAST NOTIFICATION SYSTEM
// ============================================
function useToast() {
  const [toasts, setToasts] = useState([]);
  const showToast = (message, type = 'success') => {
    const id = Date.now() + Math.random();
    const duration = type === 'error' ? 5500 : type === 'warning' ? 4500 : 3500;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
  };
  return { toasts, showToast };
}

function ToastContainer({ toasts }) {
  if (!toasts.length) return null;
  return (
    <div className="toast-container">
      {toasts.map(t => (
        <div key={t.id} className={`toast toast-${t.type}`}>
          {t.type === 'success' ? <CheckCircle2 size={16}/> : t.type === 'warning' ? <AlertCircle size={16}/> : <AlertCircle size={16}/>}
          <span>{t.message}</span>
        </div>
      ))}
    </div>
  );
}

// ============================================
// CONFIRM DIALOG
// ============================================
function ConfirmDialog({ message, confirmLabel = 'Ya, Hapus', confirmClass = 'btn-danger', onConfirm, onCancel }) {
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:99998,padding:'1rem'}}>
      <div style={{background:'white',borderRadius:'1rem',padding:'1.5rem',maxWidth:'22rem',width:'100%',boxShadow:'0 25px 50px -12px rgba(0,0,0,0.25)'}}>
        <p style={{fontSize:'0.925rem',color:'#1B2730',fontWeight:600,marginBottom:'1.25rem',lineHeight:1.6}}>{message}</p>
        <div style={{display:'flex',gap:'0.75rem',justifyContent:'flex-end'}}>
          <button className="btn-secondary" onClick={onCancel}>Batal</button>
          <button className={confirmClass} onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// MAIN APP COMPONENT
// ============================================
function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [mahasiswaPage, setMahasiswaPage] = useState(null);

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = async () => {
    await api.logout();
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }

  if (!user) {
    return <LoginPage onLogin={handleLogin} />;
  }

  if (user.role === 'admin') {
    return <AdminDashboard user={user} onLogout={handleLogout} />;
  }

  if (user.role === 'dosen' && showAnalytics) {
    return <DosenAnalyticsPage user={user} onLogout={handleLogout} onBack={() => setShowAnalytics(false)} />;
  }

  if (user.role === 'mahasiswa' && mahasiswaPage === 'riwayat') {
    return <MahasiswaRiwayatPage user={user} onLogout={handleLogout} onBack={() => setMahasiswaPage(null)} />;
  }

  if (user.role === 'mahasiswa' && mahasiswaPage === 'profil') {
    return <MahasiswaProfilPage user={user} onLogout={handleLogout} onBack={() => setMahasiswaPage(null)} />;
  }

  return (
    <AssessmentForm
      user={user}
      onLogout={handleLogout}
      onOpenAnalytics={user.role === 'dosen' ? () => setShowAnalytics(true) : undefined}
      onOpenRiwayat={user.role === 'mahasiswa' ? () => setMahasiswaPage('riwayat') : undefined}
      onOpenProfil={user.role === 'mahasiswa' ? () => setMahasiswaPage('profil') : undefined}
    />
  );
}

// ============================================
// ADMIN DASHBOARD
// ============================================
function AdminDashboard({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({});
  const [students, setStudents] = useState([]);
  const [dosens, setDosens] = useState([]);
  const [courses, setCourses] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [teachings, setTeachings] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [assessmentPagination, setAssessmentPagination] = useState({ total: 0, page: 1, limit: 50, totalPages: 1 });
  const [admins, setAdmins] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [kelasList, setKelasList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(null);
  const [formData, setFormData] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [exportFilters, setExportFilters] = useState({periodId:'',programStudi:'',formType:'',assessorRole:''});
  const [expandedStudentId, setExpandedStudentId] = useState(null);
  const [expandedDosenId, setExpandedDosenId] = useState(null);
  const [addEnrollCourseId, setAddEnrollCourseId] = useState('');
  const [addTeachCourseId, setAddTeachCourseId] = useState('');
  const [formCourseIds, setFormCourseIds] = useState([]);
  // Kelas management state
  const [expandedKelasId, setExpandedKelasId] = useState(null);
  const [kelasTab, setKelasTab] = useState('mahasiswa'); // 'mahasiswa' | 'mk'
  const [kelasMahasiswa, setKelasMahasiswa] = useState({});
  const [kelasMataKuliah, setKelasMataKuliah] = useState({});
  const [addKmStudentId, setAddKmStudentId] = useState('');
  const [addKmSemesterId, setAddKmSemesterId] = useState('');
  const [addKmCourseId, setAddKmCourseId] = useState('');
  const [addKmDosenId, setAddKmDosenId] = useState('');

  const { toasts, showToast } = useToast();
  const [confirmDialog, setConfirmDialog] = useState(null);
  const showConfirm = (message, onConfirm, options = {}) => {
    setConfirmDialog({ message, onConfirm, confirmLabel: options.confirmLabel || 'Ya, Hapus', confirmClass: options.confirmClass || 'btn-danger' });
  };

  useEffect(() => { loadData(); }, []);
  useEffect(() => {
    setSelectedIds(new Set());
    setSearchTerm('');
    setShowForm(null);
    setFormData({});
    setEditingId(null);
    setFormCourseIds([]);
    if (activeTab === 'assessments') loadAssessments();
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsRes, studentsRes, dosensRes, coursesRes, periodsRes, enrollmentsRes, teachingsRes, semestersRes, kelasRes] = await Promise.all([
        api.getStats(), api.getStudents(), api.getDosens(), api.getCourses(),
        api.getPeriods(), api.getEnrollments(), api.getTeachings(),
        api.getSemesters(), api.getKelas()
      ]);
      setStats(statsRes.stats || {});
      setStudents(studentsRes.data || []);
      setDosens(dosensRes.data || []);
      setCourses(coursesRes.data || []);
      setPeriods(periodsRes.data || []);
      setEnrollments(enrollmentsRes.data || []);
      setTeachings(teachingsRes.data || []);
      setSemesters(semestersRes.data || []);
      setKelasList(kelasRes.data || []);
      if (user.adminRole === 'super_admin') {
        const adminsRes = await api.getAdmins();
        setAdmins(adminsRes.data || []);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const loadAssessments = async (filters = {}, page = 1) => {
    try {
      const res = await api.getAssessments({ ...filters, page, limit: 50 });
      setAssessments(res.data || []);
      setAssessmentPagination(res.pagination || { total: 0, page: 1, limit: 50, totalPages: 1 });
    } catch (err) { console.error(err); }
  };

  const loadKelasMahasiswa = async (kelasId) => {
    try {
      const res = await api.getKelasMahasiswa(kelasId);
      setKelasMahasiswa(prev => ({ ...prev, [kelasId]: res.data || [] }));
    } catch (err) { console.error(err); }
  };

  const loadKelasMataKuliah = async (kelasId) => {
    try {
      const res = await api.getKelasMataKuliah(kelasId);
      setKelasMataKuliah(prev => ({ ...prev, [kelasId]: res.data || [] }));
    } catch (err) { console.error(err); }
  };

  const handleCreate = async (type) => {
    try {
      switch(type) {
        case 'student': {
          const res = await api.createStudent(formData);
          for (const courseId of formCourseIds) {
            try { await api.createEnrollment({ studentId: res.id, courseId }); } catch(e) {}
          }
          break;
        }
        case 'dosen': {
          const res = await api.createDosen(formData);
          for (const courseId of formCourseIds) {
            try { await api.createTeaching({ dosenId: res.id, courseId }); } catch(e) {}
          }
          break;
        }
        case 'course': await api.createCourse(formData); break;
        case 'period': await api.createPeriod(formData); break;
        case 'enrollment': await api.createEnrollment(formData); break;
        case 'teaching': await api.createTeaching(formData); break;
        case 'admin': await api.createAdmin(formData); break;
        case 'semester': await api.createSemester(formData); break;
        case 'kelas': await api.createKelas(formData); break;
      }
      setShowForm(null); setFormData({}); setFormCourseIds([]); loadData();
      showToast('Data berhasil ditambahkan!');
    } catch (err) { showToast(err.message, 'error'); }
  };

  const handleDelete = async (type, id) => {
    showConfirm('Yakin hapus data ini?', async () => {
      setConfirmDialog(null);
      try {
        switch(type) {
          case 'student': await api.deleteStudent(id); break;
          case 'dosen': await api.deleteDosen(id); break;
          case 'course': await api.deleteCourse(id); break;
          case 'period': await api.deletePeriod(id); break;
          case 'enrollment': await api.deleteEnrollment(id); break;
          case 'teaching': await api.deleteTeaching(id); break;
          case 'admin': await api.deleteAdmin(id); break;
          case 'semester': await api.deleteSemester(id); break;
          case 'kelas': await api.deleteKelas(id); break;
        }
        loadData(); showToast('Data berhasil dihapus!');
      } catch (err) { showToast(err.message, 'error'); }
    });
  };

  const filteredData = (data, fields) => {
    if (!searchTerm) return data;
    return data.filter(item => fields.some(f => item[f]?.toString().toLowerCase().includes(searchTerm.toLowerCase())));
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (data) => {
    if (selectedIds.size === data.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(data.map(d => d.id)));
    }
  };

  const handleUpdateAdmin = async () => {
    try {
      await api.updateAdmin(editingId, formData);
      setShowForm(null); setFormData({}); setEditingId(null);
      const adminsRes = await api.getAdmins();
      setAdmins(adminsRes.data || []);
      showToast('Admin berhasil diupdate!');
    } catch (err) { showToast(err.message, 'error'); }
  };

  const handleUpdateOwnProfile = async () => {
    try {
      await api.updateAdminProfile(formData);
      setShowForm(null); setFormData({}); setEditingId(null);
      const adminsRes = await api.getAdmins();
      setAdmins(adminsRes.data || []);
      showToast('Profil berhasil diupdate!');
    } catch (err) { showToast(err.message, 'error'); }
  };

  const handleUpdateStudent = async () => {
    try {
      await api.updateStudent(editingId, { nim: formData.nim, name: formData.name, email: formData.email, programStudi: formData.programStudi });
      setShowForm(null); setFormData({}); setEditingId(null);
      const res = await api.getStudents();
      setStudents(res.data || []);
      showToast('Mahasiswa berhasil diupdate!');
    } catch (err) { showToast(err.message, 'error'); }
  };

  const handleUpdateDosen = async () => {
    try {
      await api.updateDosen(editingId, { nip: formData.instructorType === 'CI' ? null : formData.nip, name: formData.name, email: formData.email, instructorType: formData.instructorType });
      setShowForm(null); setFormData({}); setEditingId(null);
      const res = await api.getDosens();
      setDosens(res.data || []);
      showToast('Pengajar berhasil diupdate!');
    } catch (err) { showToast(err.message, 'error'); }
  };

  const handleUpdateCourse = async () => {
    try {
      await api.updateCourse(editingId, formData);
      setShowForm(null); setFormData({}); setEditingId(null);
      const res = await api.getCourses();
      setCourses(res.data || []);
      showToast('Mata kuliah berhasil diupdate!');
    } catch (err) { showToast(err.message, 'error'); }
  };

  const handleUpdatePeriod = async () => {
    try {
      await api.updatePeriod(editingId, formData);
      setShowForm(null); setFormData({}); setEditingId(null);
      const res = await api.getPeriods();
      setPeriods(res.data || []);
      showToast('Periode berhasil diupdate!');
    } catch (err) { showToast(err.message, 'error'); }
  };

  const handleResetAdminPassword = async (id, name) => {
    showConfirm(`Reset password ${name} ke default (icare123)?`, async () => {
      setConfirmDialog(null);
      try {
        await api.resetAdminPassword(id);
        showToast(`Password ${name} berhasil direset ke: icare123`);
      } catch (err) { showToast(err.message, 'error'); }
    }, { confirmLabel: 'Ya, Reset', confirmClass: 'btn-secondary' });
  };

  const handleAddEnrollment = async (studentId) => {
    if (!addEnrollCourseId) return;
    try {
      await api.createEnrollment({ studentId, courseId: parseInt(addEnrollCourseId) });
      setAddEnrollCourseId('');
      loadData();
    } catch (err) { showToast(err.message, 'error'); }
  };

  const handleRemoveEnrollment = async (id) => {
    showConfirm('Hapus enrollment ini?', async () => {
      setConfirmDialog(null);
      try { await api.deleteEnrollment(id); loadData(); } catch (err) { showToast(err.message, 'error'); }
    });
  };

  const handleAddTeaching = async (dosenId) => {
    if (!addTeachCourseId) return;
    try {
      await api.createTeaching({ dosenId, courseId: parseInt(addTeachCourseId) });
      setAddTeachCourseId('');
      loadData();
    } catch (err) { showToast(err.message, 'error'); }
  };

  const handleRemoveTeaching = async (id) => {
    showConfirm('Hapus pengampu ini?', async () => {
      setConfirmDialog(null);
      try { await api.deleteTeaching(id); loadData(); } catch (err) { showToast(err.message, 'error'); }
    });
  };

  const handleResetPassword = async (type, id, name) => {
    showConfirm(`Reset password ${name} ke default (icare123)?`, async () => {
      setConfirmDialog(null);
      try {
        if (type === 'student') await api.resetStudentPassword(id);
        else await api.resetDosenPassword(id);
        showToast(`Password ${name} berhasil direset. Mereka akan diminta membuat password baru saat login berikutnya.`);
      } catch (err) { showToast(err.message, 'error'); }
    }, { confirmLabel: 'Ya, Reset', confirmClass: 'btn-secondary' });
  };

  const handleBulkDelete = async (type) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    showConfirm(`Hapus ${ids.length} data terpilih?`, async () => {
      setConfirmDialog(null);
      try {
        const bulkFn = {
          student: () => api.bulkDeleteStudents(ids),
          dosen: () => api.bulkDeleteDosens(ids),
          course: () => api.bulkDeleteCourses(ids),
          enrollment: () => api.bulkDeleteEnrollments(ids),
          teaching: () => api.bulkDeleteTeachings(ids),
        };
        await bulkFn[type]();
        setSelectedIds(new Set());
        loadData();
        showToast(`${ids.length} data berhasil dihapus`);
      } catch (err) { showToast(err.message, 'error'); }
    });
  };

  const TEMPLATES = {
    student:    { name: 'Template_Mahasiswa.xlsx',   headers: ['NIM','Nama','Email','Password','Program Studi'] },
    dosen:      { name: 'Template_Pengajar.xlsx',     headers: ['NIK (kosongkan jika CI)','Nama','Email','Password','Tipe (dosen/CI)'] },
    course:     { name: 'Template_MataKuliah.xlsx',  headers: ['Kode MK','Nama MK'] },
    enrollment: { name: 'Template_Enrollment.xlsx',  headers: ['NIM','Kode MK'] },
    teaching:   { name: 'Template_Pengampu.xlsx',    headers: ['NIP','Kode MK'] },
    semester:   { name: 'Template_Semester.xlsx',    headers: ['Kode','Nama','Tahun Akademik','Jenis'] },
    kelas:      { name: 'Template_Kelas.xlsx',       headers: ['Nama Kelas','Program Studi','Angkatan'] },
  };

  const downloadTemplate = (type) => {
    const tpl = TEMPLATES[type];
    const ws = XLSX.utils.aoa_to_sheet([tpl.headers, ...Array(3).fill(tpl.headers.map(() => ''))]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    XLSX.writeFile(wb, tpl.name);
  };

  const handleFileImport = async (type, e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    const data = await file.arrayBuffer();
    const wb = XLSX.read(data);
    const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' });
    if (!rows.length) { showToast('File kosong atau format tidak sesuai', 'error'); return; }

    try {
      let mapped, confirmMsg, importFn;

      if (type === 'student') {
        mapped = rows.map(r => ({ nim: String(r['NIM']||''), name: r['Nama']||'', email: r['Email']||'', password: r['Password']||'', programStudi: r['Program Studi']||'' }));
        confirmMsg = `Import ${mapped.length} mahasiswa?`;
        importFn = async () => { const res = await api.bulkImportStudents(mapped); showToast(res.message); };
      } else if (type === 'dosen') {
        mapped = rows.map(r => { const tipe=String(r['Tipe (dosen/CI)']||r['Tipe']||'').trim().toUpperCase()==='CI'?'CI':'dosen'; return { nip: String(r['NIK (kosongkan jika CI)']||r['NIP']||r['NIK']||'').trim()||null, name: r['Nama']||'', email: r['Email']||'', password: r['Password']||'', instructorType: tipe }; });
        confirmMsg = `Import ${mapped.length} pengajar?`;
        importFn = async () => { const res = await api.bulkImportDosens(mapped); showToast(res.message); };
      } else if (type === 'course') {
        mapped = rows.map(r => ({ code: String(r['Kode MK']||''), name: r['Nama MK']||'' }));
        confirmMsg = `Import ${mapped.length} mata kuliah?`;
        importFn = async () => { const res = await api.bulkImportCourses(mapped); showToast(res.message); };
      } else if (type === 'enrollment') {
        mapped = rows.map(r => ({ nim: String(r['NIM']||''), courseCode: String(r['Kode MK']||'') }));
        confirmMsg = `Import ${mapped.length} enrollment?`;
        importFn = async () => { const res = await api.bulkImportEnrollments(mapped); showToast(res.message); };
      } else if (type === 'teaching') {
        mapped = rows.map(r => ({ nip: String(r['NIP']||''), courseCode: String(r['Kode MK']||'') }));
        confirmMsg = `Import ${mapped.length} pengampu?`;
        importFn = async () => { const res = await api.bulkImportTeachings(mapped); showToast(res.message); };
      } else if (type === 'semester') {
        mapped = rows.map(r => ({ kode: String(r['Kode']||'').trim(), nama: String(r['Nama']||'').trim(), tahunAkademik: String(r['Tahun Akademik']||'').trim(), jenis: String(r['Jenis']||'ganjil').trim().toLowerCase() }));
        confirmMsg = `Import ${mapped.length} semester?`;
        importFn = async () => { const res = await api.bulkImportSemesters(mapped); showToast(res.message); };
      } else if (type === 'kelas') {
        mapped = rows.map(r => ({ nama: String(r['Nama Kelas']||'').trim(), programStudi: String(r['Program Studi']||'').trim(), angkatan: String(r['Angkatan']||'').trim() }));
        confirmMsg = `Import ${mapped.length} kelas?`;
        importFn = async () => { const res = await api.bulkImportKelas(mapped); showToast(res.message); };
      }

      if (confirmMsg && importFn) {
        showConfirm(confirmMsg, async () => {
          setConfirmDialog(null);
          try { await importFn(); loadData(); } catch (err) { showToast(err.message || 'Import gagal', 'error'); }
        }, { confirmLabel: 'Ya, Import', confirmClass: 'btn-primary' });
      }
    } catch (err) { showToast(err.message || 'Import gagal', 'error'); }
  };

  const exportAssessments = async () => {
    try {
      showToast('Menyiapkan export...', 'warning');
      const res = await api.getAssessments({ ...exportFilters, search: searchTerm, export: '1' });
      const rows = (res.data || []).map(a => ({
        'NIM': a.nim, 'Nama': a.student_name, 'Program Studi': a.program_studi,
        'Mata Kuliah': a.course_name, 'Kode MK': a.course_code,
        'Periode': a.period_name, 'Tipe Form': a.form_type,
        'Penilai': a.assessor_role, 'Total Skor': a.total_score,
        'Maks Skor': a.max_score, 'Persentase (%)': a.percentage,
        'Level': `Level ${a.level}`, 'Tanggal': a.submitted_at ? new Date(a.submitted_at).toLocaleDateString('id-ID') : ''
      }));
      if (!rows.length) { showToast('Tidak ada data untuk diexport', 'warning'); return; }
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Hasil Penilaian');
      XLSX.writeFile(wb, `Hasil_Penilaian_${new Date().toISOString().slice(0,10)}.xlsx`);
      showToast(`${rows.length} data berhasil diexport`);
    } catch (err) { showToast(err.message, 'error'); }
  };

  if (loading) return <div className="loading-screen"><div className="spinner"></div></div>;

  return (
    <div className="admin-container">
      <ToastContainer toasts={toasts}/>
      {confirmDialog && (
        <ConfirmDialog
          message={confirmDialog.message}
          confirmLabel={confirmDialog.confirmLabel}
          confirmClass={confirmDialog.confirmClass}
          onConfirm={confirmDialog.onConfirm}
          onCancel={()=>setConfirmDialog(null)}
        />
      )}
      <nav className="admin-nav">
        <div className="admin-nav-content">
          <div className="header-left">
            <div className="header-logo">I-CARE</div>
            <div><h1 className="header-title">Admin Dashboard</h1><p className="header-subtitle">{user.name}</p></div>
          </div>
          <div className="admin-nav-tabs">
            {[{k:'overview',i:BarChart3,l:'Overview'},{k:'students',i:Users,l:'Mahasiswa'},{k:'dosens',i:GraduationCap,l:'Pengajar'},
              {k:'courses',i:BookOpen,l:'MK'},{k:'periods',i:Calendar,l:'Periode'},
              {k:'semesters',i:Tag,l:'Semester'},{k:'kelas',i:School,l:'Kelas'},
              {k:'analitik',i:TrendingUp,l:'Analitik'},
              {k:'assessments',i:FileText,l:'Hasil'},
              ...(user.adminRole === 'super_admin' ? [{k:'admins',i:UserCog,l:'Admin'},{k:'auditlog',i:ClipboardList,l:'Audit Log'}] : [])].map(t => (
              <button key={t.k} className={`admin-tab ${activeTab===t.k?'active':''}`} onClick={()=>setActiveTab(t.k)}>
                <t.i size={16}/><span className="tab-label">{t.l}</span>
              </button>
            ))}
          </div>
          <button onClick={onLogout} className="btn-logout"><LogOut size={16}/><span>Logout</span></button>
        </div>
      </nav>

      <div className="content-container">
        {/* Search */}
        <div className="card" style={{marginBottom:'1.5rem'}}>
          <div className="input-wrapper">
            <Search className="input-icon" size={20}/>
            <input type="text" value={searchTerm} onChange={e=>setSearchTerm(e.target.value)} className="form-input" placeholder="Cari..." style={{paddingLeft:'3rem'}}/>
          </div>
        </div>

        {/* Overview */}
        {activeTab === 'overview' && (
          <div className="admin-stats-grid">
            {[
              {l:'Mahasiswa',v:stats.totalStudents,Icon:Users},
              {l:'Pengajar',v:stats.totalDosens,Icon:GraduationCap},
              {l:'Mata Kuliah',v:stats.totalCourses,Icon:BookOpen},
              {l:'Penilaian',v:stats.totalAssessments,Icon:FileText},
              {l:'Enrollment',v:stats.totalEnrollments,Icon:Link},
              {l:'Pengampu',v:stats.totalTeachings,Icon:UserCheck}
            ].map((s,i) => (
              <div key={i} className="stat-card">
                <div className="stat-header">
                  <div className="stat-icon"><s.Icon size={16}/></div>
                  <div className="stat-title">{s.l}</div>
                </div>
                <div className="stat-value">{s.v||0}</div>
              </div>
            ))}
          </div>
        )}

        {/* Students */}
        {activeTab === 'students' && (
          <div className="card">
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:'1rem',flexWrap:'wrap',gap:'0.75rem',alignItems:'center'}}>
              <h3 className="card-title" style={{margin:0}}><Users size={20}/>Data Mahasiswa</h3>
              <div style={{display:'flex',gap:'0.5rem',flexWrap:'wrap'}}>
                {selectedIds.size > 0 && <button className="btn-danger" onClick={()=>handleBulkDelete('student')}><Trash2 size={14}/>Hapus ({selectedIds.size})</button>}
                <button className="btn-secondary" onClick={()=>downloadTemplate('student')}><Download size={14}/>Template</button>
                <label className="btn-success" style={{cursor:'pointer'}}><Upload size={14}/>Import Mahasiswa<input type="file" accept=".xlsx,.xls" style={{display:'none'}} onChange={e=>handleFileImport('student',e)}/></label>
                <button className="btn-secondary" onClick={()=>downloadTemplate('enrollment')}><Download size={14}/>Tpl Enrollment</button>
                <label className="btn-success" style={{cursor:'pointer'}}><Upload size={14}/>Import Enrollment<input type="file" accept=".xlsx,.xls" style={{display:'none'}} onChange={e=>handleFileImport('enrollment',e)}/></label>
                <button className="btn-primary" onClick={()=>{setShowForm(showForm==='student'?null:'student');setFormData({});setFormCourseIds([]);setEditingId(null);}}><Plus size={16}/>Tambah</button>
              </div>
            </div>
            {showForm === 'student' && (
              <div style={{background:'#F5F7F6',padding:'1rem',borderRadius:'0.5rem',marginBottom:'1rem'}}>
                <div style={{fontWeight:600,fontSize:'0.9rem',marginBottom:'0.75rem',color:'#3A474F'}}>{editingId ? 'Edit Mahasiswa' : 'Tambah Mahasiswa'}</div>
                <div className="period-form-grid">
                  <input placeholder="NIM" className="period-input" value={formData.nim||''} onChange={e=>setFormData({...formData,nim:e.target.value})}/>
                  <input placeholder="Nama" className="period-input" value={formData.name||''} onChange={e=>setFormData({...formData,name:e.target.value})}/>
                  <input placeholder="Email" className="period-input" value={formData.email||''} onChange={e=>setFormData({...formData,email:e.target.value})}/>
                  {!editingId && <input placeholder="Password (opsional, kosongkan untuk password acak)" type="password" className="period-input" value={formData.password||''} onChange={e=>setFormData({...formData,password:e.target.value})}/>}
                  <input placeholder="Program Studi" className="period-input" value={formData.programStudi||''} onChange={e=>setFormData({...formData,programStudi:e.target.value})}/>
                </div>
                {!editingId && (
                  <div style={{marginTop:'0.75rem'}}>
                    <div style={{fontSize:'0.85rem',fontWeight:600,color:'#3A474F',marginBottom:'0.4rem'}}>Mata Kuliah (opsional)</div>
                    <select className="period-input" style={{maxWidth:'320px'}} onChange={e=>{if(e.target.value){setFormCourseIds(prev=>[...new Set([...prev,parseInt(e.target.value)])]);e.target.value='';}}} defaultValue="">
                      <option value="">Tambah mata kuliah...</option>
                      {courses.filter(c=>!formCourseIds.includes(c.id)).map(c=><option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}
                    </select>
                    {formCourseIds.length > 0 && (
                      <div style={{display:'flex',flexWrap:'wrap',gap:'0.4rem',marginTop:'0.4rem'}}>
                        {formCourseIds.map(id=>{const c=courses.find(x=>x.id===id);return c?(<span key={id} style={{display:'inline-flex',alignItems:'center',gap:'0.3rem',background:'#E6ECF4',color:'#2D5B8C',borderRadius:'0.375rem',padding:'0.2rem 0.6rem',fontSize:'0.8rem'}}>{c.code} — {c.name}<button type="button" onClick={()=>setFormCourseIds(prev=>prev.filter(i=>i!==id))} style={{background:'none',border:'none',cursor:'pointer',color:'#2D5B8C',padding:0,display:'flex'}}><X size={12}/></button></span>):null;})}
                      </div>
                    )}
                  </div>
                )}
                <div style={{display:'flex',gap:'0.5rem',marginTop:'1rem'}}>
                  <button className="btn-primary" onClick={()=>editingId ? handleUpdateStudent() : handleCreate('student')}>Simpan</button>
                  <button className="btn-secondary" onClick={()=>{setShowForm(null);setFormCourseIds([]);setEditingId(null);}}>Batal</button>
                </div>
              </div>
            )}
            <div className="table-container"><div className="table-wrapper"><table className="data-table">
              <thead><tr>
                <th style={{width:'2.5rem'}}><input type="checkbox" checked={selectedIds.size===filteredData(students,['nim','name','email','program_studi']).length && filteredData(students,['nim','name','email','program_studi']).length>0} onChange={()=>toggleSelectAll(filteredData(students,['nim','name','email','program_studi']))}/></th>
                <th>NIM</th><th>Nama</th><th>Email</th><th>Prodi</th><th>Aksi</th>
              </tr></thead>
              <tbody>
                {filteredData(students,['nim','name','email','program_studi']).map(s=>(
                  <React.Fragment key={s.id}>
                    <tr style={{background:selectedIds.has(s.id)?'#F2F5FA':''}}>
                      <td><input type="checkbox" checked={selectedIds.has(s.id)} onChange={()=>toggleSelect(s.id)}/></td>
                      <td>{s.nim}</td><td>{s.name}</td><td>{s.email}</td><td>{s.program_studi}</td>
                      <td style={{display:'flex',gap:'0.35rem'}}>
                        <button className="btn-secondary" title="Kelola Enrollment" style={{padding:'0.25rem 0.5rem'}} onClick={()=>{setExpandedStudentId(expandedStudentId===s.id?null:s.id);setAddEnrollCourseId('');}}>
                          {expandedStudentId===s.id?<ChevronUp size={14}/>:<ChevronDown size={14}/>}
                        </button>
                        <button className="btn-secondary" title="Edit" style={{padding:'0.25rem 0.5rem'}} onClick={()=>{setEditingId(s.id);setFormData({nim:s.nim,name:s.name,email:s.email,programStudi:s.program_studi});setShowForm('student');setExpandedStudentId(null);}}><Edit size={14}/></button>
                        <button className="btn-secondary" title="Reset Password" onClick={()=>handleResetPassword('student',s.id,s.name)} style={{padding:'0.25rem 0.5rem'}}><KeyRound size={14}/></button>
                        <button className="btn-danger" onClick={()=>handleDelete('student',s.id)}><Trash2 size={14}/></button>
                      </td>
                    </tr>
                    {expandedStudentId===s.id && (
                      <tr><td colSpan={6} style={{padding:'0.75rem 1rem',background:'#F2F5FA',borderBottom:'2px solid #C5D3E6'}}>
                        <div style={{fontWeight:600,fontSize:'0.85rem',marginBottom:'0.5rem',color:'#2D5B8C'}}>Enrollment MK — {s.name}</div>
                        <div style={{display:'flex',flexWrap:'wrap',gap:'0.4rem',marginBottom:'0.6rem'}}>
                          {enrollments.filter(e=>e.student_id===s.id).length===0
                            ? <span style={{color:'#7C8990',fontSize:'0.8rem'}}>Belum ada enrollment</span>
                            : enrollments.filter(e=>e.student_id===s.id).map(e=>(
                              <span key={e.id} style={{display:'inline-flex',alignItems:'center',gap:'0.3rem',background: e.source==='kelas'?'#E4EFE7':'#E6ECF4',color: e.source==='kelas'?'#2F6E45':'#2D5B8C',borderRadius:'0.375rem',padding:'0.2rem 0.6rem',fontSize:'0.8rem'}}>
                                {e.course_code} — {e.course_name}
                                <span style={{fontSize:'0.65rem',opacity:0.8}}>[{e.source==='kelas'?'kelas':'manual'}]</span>
                                {e.source==='manual' && (
                                  <button onClick={()=>handleRemoveEnrollment(e.id)} style={{background:'none',border:'none',cursor:'pointer',color:'#2D5B8C',padding:0,display:'flex',alignItems:'center'}}><X size={12}/></button>
                                )}
                                {e.source==='kelas' && (
                                  <span title="Dikelola via Kelas" style={{opacity:0.6,display:'flex',alignItems:'center'}}><School size={10}/></span>
                                )}
                              </span>
                            ))
                          }
                        </div>
                        <div style={{display:'flex',gap:'0.5rem'}}>
                          <select value={addEnrollCourseId} onChange={e=>setAddEnrollCourseId(e.target.value)} className="period-input" style={{flex:1,maxWidth:'320px'}}>
                            <option value="">Tambah mata kuliah...</option>
                            {courses.filter(c=>!enrollments.find(e=>e.student_id===s.id&&e.course_id===c.id)).map(c=>(
                              <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
                            ))}
                          </select>
                          <button className="btn-primary" style={{padding:'0.4rem 0.75rem',fontSize:'0.8rem'}} onClick={()=>handleAddEnrollment(s.id)}><Plus size={14}/>Tambah</button>
                        </div>
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table></div></div>
          </div>
        )}

        {/* Dosens */}
        {activeTab === 'dosens' && (
          <div className="card">
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:'1rem',flexWrap:'wrap',gap:'0.75rem',alignItems:'center'}}>
              <h3 className="card-title" style={{margin:0}}><GraduationCap size={20}/>Data Pengajar</h3>
              <div style={{display:'flex',gap:'0.5rem',flexWrap:'wrap'}}>
                {selectedIds.size > 0 && <button className="btn-danger" onClick={()=>handleBulkDelete('dosen')}><Trash2 size={14}/>Hapus ({selectedIds.size})</button>}
                <button className="btn-secondary" onClick={()=>downloadTemplate('dosen')}><Download size={14}/>Template</button>
                <label className="btn-success" style={{cursor:'pointer'}}><Upload size={14}/>Import Pengajar<input type="file" accept=".xlsx,.xls" style={{display:'none'}} onChange={e=>handleFileImport('dosen',e)}/></label>
                <button className="btn-secondary" onClick={()=>downloadTemplate('teaching')}><Download size={14}/>Tpl Pengampu</button>
                <label className="btn-success" style={{cursor:'pointer'}}><Upload size={14}/>Import Pengampu<input type="file" accept=".xlsx,.xls" style={{display:'none'}} onChange={e=>handleFileImport('teaching',e)}/></label>
                <button className="btn-primary" onClick={()=>{setShowForm(showForm==='dosen'?null:'dosen');setFormData({});setFormCourseIds([]);setEditingId(null);}}><Plus size={16}/>Tambah</button>
              </div>
            </div>
            {showForm === 'dosen' && (
              <div style={{background:'#F5F7F6',padding:'1rem',borderRadius:'0.5rem',marginBottom:'1rem'}}>
                <div style={{fontWeight:600,fontSize:'0.9rem',marginBottom:'0.75rem',color:'#3A474F'}}>{editingId ? 'Edit Pengajar' : 'Tambah Pengajar'}</div>
                <div className="period-form-grid">
                  <select className="period-input" value={formData.instructorType||'dosen'} onChange={e=>setFormData({...formData,instructorType:e.target.value,nip:e.target.value==='CI'?'':formData.nip})}>
                    <option value="dosen">Dosen</option>
                    <option value="CI">CI (Clinical Instructor)</option>
                  </select>
                  <input placeholder="Nama" className="period-input" value={formData.name||''} onChange={e=>setFormData({...formData,name:e.target.value})}/>
                  <input placeholder="Email" className="period-input" value={formData.email||''} onChange={e=>setFormData({...formData,email:e.target.value})}/>
                  {(formData.instructorType||'dosen')==='dosen' && (
                    <input placeholder="NIK (Nomor Induk Karyawan)" className="period-input" value={formData.nip||''} onChange={e=>setFormData({...formData,nip:e.target.value})}/>
                  )}
                  {!editingId && <input placeholder="Password (opsional, kosongkan untuk password acak)" type="password" className="period-input" value={formData.password||''} onChange={e=>setFormData({...formData,password:e.target.value})}/>}
                </div>
                {!editingId && (
                  <div style={{marginTop:'0.75rem'}}>
                    <div style={{fontSize:'0.85rem',fontWeight:600,color:'#3A474F',marginBottom:'0.4rem'}}>Mata Kuliah Pengampu (opsional)</div>
                    <select className="period-input" style={{maxWidth:'320px'}} onChange={e=>{if(e.target.value){setFormCourseIds(prev=>[...new Set([...prev,parseInt(e.target.value)])]);e.target.value='';}}} defaultValue="">
                      <option value="">Tambah mata kuliah...</option>
                      {courses.filter(c=>!formCourseIds.includes(c.id)).map(c=><option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}
                    </select>
                    {formCourseIds.length > 0 && (
                      <div style={{display:'flex',flexWrap:'wrap',gap:'0.4rem',marginTop:'0.4rem'}}>
                        {formCourseIds.map(id=>{const c=courses.find(x=>x.id===id);return c?(<span key={id} style={{display:'inline-flex',alignItems:'center',gap:'0.3rem',background:'#ECE8F3',color:'#5E4B8B',borderRadius:'0.375rem',padding:'0.2rem 0.6rem',fontSize:'0.8rem'}}>{c.code} — {c.name}<button type="button" onClick={()=>setFormCourseIds(prev=>prev.filter(i=>i!==id))} style={{background:'none',border:'none',cursor:'pointer',color:'#5E4B8B',padding:0,display:'flex'}}><X size={12}/></button></span>):null;})}
                      </div>
                    )}
                  </div>
                )}
                <div style={{display:'flex',gap:'0.5rem',marginTop:'1rem'}}>
                  <button className="btn-primary" onClick={()=>editingId ? handleUpdateDosen() : handleCreate('dosen')}>Simpan</button>
                  <button className="btn-secondary" onClick={()=>{setShowForm(null);setFormCourseIds([]);setEditingId(null);}}>Batal</button>
                </div>
              </div>
            )}
            <div className="table-container"><div className="table-wrapper"><table className="data-table">
              <thead><tr>
                <th style={{width:'2.5rem'}}><input type="checkbox" checked={selectedIds.size===filteredData(dosens,['nip','name','email']).length && filteredData(dosens,['nip','name','email']).length>0} onChange={()=>toggleSelectAll(filteredData(dosens,['nip','name','email']))}/></th>
                <th>NIK</th><th>Nama</th><th>Tipe</th><th>Email</th><th>Aksi</th>
              </tr></thead>
              <tbody>
                {filteredData(dosens,['nip','name','email']).map(d=>(
                  <React.Fragment key={d.id}>
                    <tr style={{background:selectedIds.has(d.id)?'#F2F5FA':''}}>
                      <td><input type="checkbox" checked={selectedIds.has(d.id)} onChange={()=>toggleSelect(d.id)}/></td>
                      <td>{d.nip || <span style={{color:'#7C8990',fontSize:'0.8rem'}}>—</span>}</td><td>{d.name}</td>
                      <td><span style={{display:'inline-block',padding:'0.15rem 0.5rem',borderRadius:'0.375rem',fontSize:'0.75rem',fontWeight:600,background:d.instructor_type==='CI'?'#E4EFE7':'#ECE8F3',color:d.instructor_type==='CI'?'#24563A':'#5E4B8B'}}>{d.instructor_type==='CI'?'CI':'Dosen'}</span></td>
                      <td>{d.email}</td>
                      <td style={{display:'flex',gap:'0.35rem'}}>
                        <button className="btn-secondary" title="Kelola Pengampu" style={{padding:'0.25rem 0.5rem'}} onClick={()=>{setExpandedDosenId(expandedDosenId===d.id?null:d.id);setAddTeachCourseId('');}}>
                          {expandedDosenId===d.id?<ChevronUp size={14}/>:<ChevronDown size={14}/>}
                        </button>
                        <button className="btn-secondary" title="Edit" style={{padding:'0.25rem 0.5rem'}} onClick={()=>{setEditingId(d.id);setFormData({nip:d.nip,name:d.name,email:d.email,instructorType:d.instructor_type||'dosen'});setShowForm('dosen');setExpandedDosenId(null);}}><Edit size={14}/></button>
                        <button className="btn-secondary" title="Reset Password" onClick={()=>handleResetPassword('dosen',d.id,d.name)} style={{padding:'0.25rem 0.5rem'}}><KeyRound size={14}/></button>
                        <button className="btn-danger" onClick={()=>handleDelete('dosen',d.id)}><Trash2 size={14}/></button>
                      </td>
                    </tr>
                    {expandedDosenId===d.id && (
                      <tr><td colSpan={6} style={{padding:'0.75rem 1rem',background:'#F5F3F9',borderBottom:'2px solid #CBC2DE'}}>
                        <div style={{fontWeight:600,fontSize:'0.85rem',marginBottom:'0.5rem',color:'#5E4B8B'}}>Pengampu MK — {d.name}</div>
                        <div style={{display:'flex',flexWrap:'wrap',gap:'0.4rem',marginBottom:'0.6rem'}}>
                          {teachings.filter(t=>t.dosen_id===d.id).length===0
                            ? <span style={{color:'#7C8990',fontSize:'0.8rem'}}>Belum ada pengampu</span>
                            : teachings.filter(t=>t.dosen_id===d.id).map(t=>(
                              <span key={t.id} style={{display:'inline-flex',alignItems:'center',gap:'0.3rem',background: t.source==='kelas'?'#E4EFE7':'#ECE8F3',color: t.source==='kelas'?'#2F6E45':'#5E4B8B',borderRadius:'0.375rem',padding:'0.2rem 0.6rem',fontSize:'0.8rem'}}>
                                {t.course_code} — {t.course_name}
                                <span style={{fontSize:'0.65rem',opacity:0.8}}>[{t.source==='kelas'?'kelas':'manual'}]</span>
                                {t.source==='manual' && (
                                  <button onClick={()=>handleRemoveTeaching(t.id)} style={{background:'none',border:'none',cursor:'pointer',color:'#5E4B8B',padding:0,display:'flex',alignItems:'center'}}><X size={12}/></button>
                                )}
                                {t.source==='kelas' && (
                                  <span title="Dikelola via Kelas" style={{opacity:0.6,display:'flex',alignItems:'center'}}><School size={10}/></span>
                                )}
                              </span>
                            ))
                          }
                        </div>
                        <div style={{display:'flex',gap:'0.5rem'}}>
                          <select value={addTeachCourseId} onChange={e=>setAddTeachCourseId(e.target.value)} className="period-input" style={{flex:1,maxWidth:'320px'}}>
                            <option value="">Tambah mata kuliah pengampu...</option>
                            {courses.filter(c=>!teachings.find(t=>t.dosen_id===d.id&&t.course_id===c.id)).map(c=>(
                              <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
                            ))}
                          </select>
                          <button className="btn-primary" style={{padding:'0.4rem 0.75rem',fontSize:'0.8rem'}} onClick={()=>handleAddTeaching(d.id)}><Plus size={14}/>Tambah</button>
                        </div>
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table></div></div>
          </div>
        )}

        {/* Courses */}
        {activeTab === 'courses' && (
          <div className="card">
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:'1rem',flexWrap:'wrap',gap:'0.75rem',alignItems:'center'}}>
              <h3 className="card-title" style={{margin:0}}><BookOpen size={20}/>Data Mata Kuliah</h3>
              <div style={{display:'flex',gap:'0.5rem',flexWrap:'wrap'}}>
                {selectedIds.size > 0 && <button className="btn-danger" onClick={()=>handleBulkDelete('course')}><Trash2 size={14}/>Hapus ({selectedIds.size})</button>}
                <button className="btn-secondary" onClick={()=>downloadTemplate('course')}><Download size={14}/>Template</button>
                <label className="btn-success" style={{cursor:'pointer'}}><Upload size={14}/>Import Excel<input type="file" accept=".xlsx,.xls" style={{display:'none'}} onChange={e=>handleFileImport('course',e)}/></label>
                <button className="btn-primary" onClick={()=>{setShowForm(showForm==='course'?null:'course');setFormData({});setEditingId(null);}}><Plus size={16}/>Tambah</button>
              </div>
            </div>
            {showForm === 'course' && (
              <div style={{background:'#F5F7F6',padding:'1rem',borderRadius:'0.5rem',marginBottom:'1rem'}}>
                <div style={{fontWeight:600,fontSize:'0.9rem',marginBottom:'0.75rem',color:'#3A474F'}}>{editingId ? 'Edit Mata Kuliah' : 'Tambah Mata Kuliah'}</div>
                <div className="period-form-grid">
                  <input placeholder="Kode MK" className="period-input" value={formData.code||''} onChange={e=>setFormData({...formData,code:e.target.value})}/>
                  <input placeholder="Nama MK" className="period-input" value={formData.name||''} onChange={e=>setFormData({...formData,name:e.target.value})}/>
                </div>
                <div style={{display:'flex',gap:'0.5rem',marginTop:'1rem'}}>
                  <button className="btn-primary" onClick={()=>editingId ? handleUpdateCourse() : handleCreate('course')}>Simpan</button>
                  <button className="btn-secondary" onClick={()=>{setShowForm(null);setEditingId(null);setFormData({});}}>Batal</button>
                </div>
              </div>
            )}
            <div className="table-container"><div className="table-wrapper"><table className="data-table">
              <thead><tr>
                <th style={{width:'2.5rem'}}><input type="checkbox" checked={selectedIds.size===filteredData(courses,['code','name']).length && filteredData(courses,['code','name']).length>0} onChange={()=>toggleSelectAll(filteredData(courses,['code','name']))}/></th>
                <th>Kode</th><th>Nama</th><th>Aksi</th>
              </tr></thead>
              <tbody>
                {filteredData(courses,['code','name']).map(c=>(
                  <tr key={c.id} style={{background:selectedIds.has(c.id)?'#F2F5FA':''}}>
                    <td><input type="checkbox" checked={selectedIds.has(c.id)} onChange={()=>toggleSelect(c.id)}/></td>
                    <td>{c.code}</td><td>{c.name}</td>
                    <td style={{display:'flex',gap:'0.35rem'}}>
                      <button className="btn-secondary" style={{padding:'0.25rem 0.5rem'}} title="Edit" onClick={()=>{setEditingId(c.id);setFormData({code:c.code,name:c.name});setShowForm('course');}}><Edit size={14}/></button>
                      <button className="btn-danger" onClick={()=>handleDelete('course',c.id)}><Trash2 size={14}/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div></div>
          </div>
        )}

        {/* Periods */}
        {activeTab === 'periods' && (
          <div className="card">
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:'1rem'}}>
              <h3 className="card-title"><Calendar size={20}/>Periode Penilaian</h3>
              <button className="btn-primary" onClick={()=>{setShowForm('period');setFormData({isActive:false});setEditingId(null);}}><Plus size={16}/>Tambah</button>
            </div>
            {showForm === 'period' && (
              <div style={{background:'#F5F7F6',padding:'1rem',borderRadius:'0.5rem',marginBottom:'1rem'}}>
                <div style={{fontWeight:600,fontSize:'0.9rem',marginBottom:'0.75rem',color:'#3A474F'}}>{editingId ? 'Edit Periode' : 'Tambah Periode'}</div>
                <div className="period-form-grid">
                  <input placeholder="Nama Periode" className="period-input" value={formData.name||''} onChange={e=>setFormData({...formData,name:e.target.value})}/>
                  <input type="date" className="period-input" value={formData.startDate||''} onChange={e=>setFormData({...formData,startDate:e.target.value})}/>
                  <input type="date" className="period-input" value={formData.endDate||''} onChange={e=>setFormData({...formData,endDate:e.target.value})}/>
                  <select className="period-input" value={formData.isActive} onChange={e=>setFormData({...formData,isActive:e.target.value==='true'})}>
                    <option value="false">Tidak Aktif</option><option value="true">Aktif</option>
                  </select>
                </div>
                <div style={{display:'flex',gap:'0.5rem',marginTop:'1rem'}}>
                  <button className="btn-primary" onClick={()=>editingId ? handleUpdatePeriod() : handleCreate('period')}>Simpan</button>
                  <button className="btn-secondary" onClick={()=>{setShowForm(null);setEditingId(null);setFormData({});}}>Batal</button>
                </div>
              </div>
            )}
            <div className="table-container"><table className="data-table">
              <thead><tr><th>Nama</th><th>Mulai</th><th>Selesai</th><th>Status</th><th>Aksi</th></tr></thead>
              <tbody>
                {periods.map(p=>(
                  <tr key={p.id}><td>{p.name}</td>
                    <td>{p.start_date ? new Date(p.start_date).toLocaleDateString('id-ID',{day:'numeric',month:'short',year:'numeric'}) : '—'}</td>
                    <td>{p.end_date ? new Date(p.end_date).toLocaleDateString('id-ID',{day:'numeric',month:'short',year:'numeric'}) : '—'}</td>
                    <td><span className={`badge ${p.is_active?'badge-success':'badge-gray'}`}>{p.is_active?'Aktif':'Tidak Aktif'}</span></td>
                    <td style={{display:'flex',gap:'0.35rem'}}>
                      <button className="btn-secondary" style={{padding:'0.25rem 0.5rem'}} title="Edit" onClick={()=>{setEditingId(p.id);setFormData({name:p.name,startDate:p.start_date?p.start_date.slice(0,10):'',endDate:p.end_date?p.end_date.slice(0,10):'',isActive:!!p.is_active});setShowForm('period');}}><Edit size={14}/></button>
                      <button className="btn-danger" onClick={()=>handleDelete('period',p.id)}><Trash2 size={14}/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>
        )}

        {/* Semesters */}
        {activeTab === 'semesters' && (
          <div className="card">
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:'1rem',alignItems:'center',flexWrap:'wrap',gap:'0.75rem'}}>
              <h3 className="card-title" style={{margin:0}}><Tag size={20}/>Manajemen Semester</h3>
              <div style={{display:'flex',gap:'0.5rem',flexWrap:'wrap'}}>
                <button className="btn-secondary" onClick={()=>downloadTemplate('semester')}><Download size={14}/>Template</button>
                <label className="btn-success" style={{cursor:'pointer'}}><Upload size={14}/>Import Excel<input type="file" accept=".xlsx,.xls" style={{display:'none'}} onChange={e=>handleFileImport('semester',e)}/></label>
                <button className="btn-primary" onClick={()=>{setShowForm(showForm==='semester'?null:'semester');setFormData({jenis:'ganjil',isActive:false});setEditingId(null);}}>
                  <Plus size={16}/>Tambah Semester
                </button>
              </div>
            </div>
            {showForm === 'semester' && (
              <div style={{background:'#F5F7F6',padding:'1rem',borderRadius:'0.5rem',marginBottom:'1rem'}}>
                <div style={{fontWeight:600,fontSize:'0.9rem',marginBottom:'0.75rem',color:'#3A474F'}}>{editingId?'Edit Semester':'Tambah Semester'}</div>
                <div className="period-form-grid">
                  <input placeholder="Kode (e.g. 2024-1)" className="period-input" value={formData.kode||''} onChange={e=>setFormData({...formData,kode:e.target.value})}/>
                  <input placeholder="Nama (e.g. Semester Ganjil 2024/2025)" className="period-input" value={formData.nama||''} onChange={e=>setFormData({...formData,nama:e.target.value})}/>
                  <input placeholder="Tahun Akademik (e.g. 2024/2025)" className="period-input" value={formData.tahunAkademik||''} onChange={e=>setFormData({...formData,tahunAkademik:e.target.value})}/>
                  <select className="period-input" value={formData.jenis||'ganjil'} onChange={e=>setFormData({...formData,jenis:e.target.value})}>
                    <option value="ganjil">Ganjil</option>
                    <option value="genap">Genap</option>
                  </select>
                  <select className="period-input" value={formData.isActive?'true':'false'} onChange={e=>setFormData({...formData,isActive:e.target.value==='true'})}>
                    <option value="false">Tidak Aktif</option>
                    <option value="true">Aktif</option>
                  </select>
                </div>
                <div style={{display:'flex',gap:'0.5rem',marginTop:'1rem'}}>
                  <button className="btn-primary" onClick={()=>{
                    if (editingId) {
                      api.updateSemester(editingId, formData).then(()=>{setShowForm(null);setFormData({});setEditingId(null);loadData();showToast('Semester diupdate!');}).catch(e=>showToast(e.message,'error'));
                    } else { handleCreate('semester'); }
                  }}>Simpan</button>
                  <button className="btn-secondary" onClick={()=>{setShowForm(null);setFormData({});setEditingId(null);}}>Batal</button>
                </div>
              </div>
            )}
            <div className="table-container"><table className="data-table">
              <thead><tr><th>Kode</th><th>Nama</th><th>Tahun Akademik</th><th>Jenis</th><th>Jumlah MK</th><th>Status</th><th>Aksi</th></tr></thead>
              <tbody>
                {filteredData(semesters,['kode','nama','tahun_akademik']).map(s=>(
                  <tr key={s.id}>
                    <td>{s.kode}</td><td>{s.nama}</td><td>{s.tahun_akademik}</td>
                    <td><span className={`badge ${s.jenis==='ganjil'?'badge-info':'badge-warning'}`}>{s.jenis}</span></td>
                    <td>{s.mk_count}</td>
                    <td><span className={`badge ${s.is_active?'badge-success':'badge-gray'}`}>{s.is_active?'Aktif':'Tidak Aktif'}</span></td>
                    <td style={{display:'flex',gap:'0.35rem'}}>
                      <button className="btn-secondary" style={{padding:'0.25rem 0.5rem'}} onClick={()=>{setEditingId(s.id);setFormData({kode:s.kode,nama:s.nama,tahunAkademik:s.tahun_akademik,jenis:s.jenis,isActive:!!s.is_active});setShowForm('semester');}}><Edit size={14}/></button>
                      <button className="btn-danger" onClick={()=>handleDelete('semester',s.id)}><Trash2 size={14}/></button>
                    </td>
                  </tr>
                ))}
                {semesters.length===0&&<tr><td colSpan={7} className="table-empty">Belum ada semester</td></tr>}
              </tbody>
            </table></div>
          </div>
        )}

        {/* Kelas */}
        {activeTab === 'kelas' && (
          <div className="card">
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:'1rem',alignItems:'center',flexWrap:'wrap',gap:'0.75rem'}}>
              <h3 className="card-title" style={{margin:0}}><School size={20}/>Manajemen Kelas</h3>
              <div style={{display:'flex',gap:'0.5rem',flexWrap:'wrap'}}>
                <button className="btn-secondary" onClick={()=>downloadTemplate('kelas')}><Download size={14}/>Template</button>
                <label className="btn-success" style={{cursor:'pointer'}}><Upload size={14}/>Import Excel<input type="file" accept=".xlsx,.xls" style={{display:'none'}} onChange={e=>handleFileImport('kelas',e)}/></label>
                <button className="btn-primary" onClick={()=>{setShowForm(showForm==='kelas'?null:'kelas');setFormData({angkatan:new Date().getFullYear()});setEditingId(null);}}>
                  <Plus size={16}/>Tambah Kelas
                </button>
              </div>
            </div>
            <p style={{fontSize:'0.85rem',color:'#55636B',marginBottom:'1rem'}}>
              Kelas mengelompokkan mahasiswa. Assign MK+Dosen ke kelas untuk auto-generate enrollment dan pengampu.
            </p>
            {showForm === 'kelas' && (
              <div style={{background:'#F5F7F6',padding:'1rem',borderRadius:'0.5rem',marginBottom:'1rem'}}>
                <div style={{fontWeight:600,fontSize:'0.9rem',marginBottom:'0.75rem',color:'#3A474F'}}>{editingId?'Edit Kelas':'Tambah Kelas'}</div>
                <div className="period-form-grid">
                  <input placeholder="Nama Kelas (e.g. Reguler A)" className="period-input" value={formData.nama||''} onChange={e=>setFormData({...formData,nama:e.target.value})}/>
                  <input placeholder="Program Studi" className="period-input" value={formData.programStudi||''} onChange={e=>setFormData({...formData,programStudi:e.target.value})}/>
                  <input type="number" placeholder="Angkatan (e.g. 2023)" className="period-input" value={formData.angkatan||''} onChange={e=>setFormData({...formData,angkatan:e.target.value})}/>
                </div>
                <div style={{display:'flex',gap:'0.5rem',marginTop:'1rem'}}>
                  <button className="btn-primary" onClick={()=>{
                    if (editingId) {
                      api.updateKelas(editingId, formData).then(()=>{setShowForm(null);setFormData({});setEditingId(null);loadData();showToast('Kelas diupdate!');}).catch(e=>showToast(e.message,'error'));
                    } else { handleCreate('kelas'); }
                  }}>Simpan</button>
                  <button className="btn-secondary" onClick={()=>{setShowForm(null);setFormData({});setEditingId(null);}}>Batal</button>
                </div>
              </div>
            )}

            <div className="table-container"><div className="table-wrapper"><table className="data-table">
              <thead><tr><th>Nama</th><th>Program Studi</th><th>Angkatan</th><th>Mahasiswa</th><th>MK</th><th>Aksi</th></tr></thead>
              <tbody>
                {filteredData(kelasList,['nama','program_studi']).map(k=>(
                  <React.Fragment key={k.id}>
                    <tr>
                      <td>{k.nama}</td><td>{k.program_studi}</td><td>{k.angkatan}</td>
                      <td>{k.jumlah_mahasiswa}</td><td>{k.jumlah_mk}</td>
                      <td style={{display:'flex',gap:'0.35rem'}}>
                        <button className="btn-secondary" title="Kelola Kelas" style={{padding:'0.25rem 0.5rem'}} onClick={()=>{
                          if (expandedKelasId===k.id) { setExpandedKelasId(null); } else {
                            setExpandedKelasId(k.id);
                            setKelasTab('mahasiswa');
                            loadKelasMahasiswa(k.id);
                            loadKelasMataKuliah(k.id);
                          }
                          setAddKmStudentId('');setAddKmSemesterId('');setAddKmCourseId('');setAddKmDosenId('');
                        }}>
                          {expandedKelasId===k.id?<ChevronUp size={14}/>:<ChevronDown size={14}/>}
                        </button>
                        <button className="btn-secondary" style={{padding:'0.25rem 0.5rem'}} onClick={()=>{setEditingId(k.id);setFormData({nama:k.nama,programStudi:k.program_studi,angkatan:k.angkatan});setShowForm('kelas');setExpandedKelasId(null);}}><Edit size={14}/></button>
                        <button className="btn-danger" onClick={()=>handleDelete('kelas',k.id)}><Trash2 size={14}/></button>
                      </td>
                    </tr>
                    {expandedKelasId===k.id && (
                      <tr><td colSpan={6} style={{padding:'1rem',background:'#F2F5FA',borderBottom:'2px solid #C5D3E6'}}>
                        {/* Sub-tab */}
                        <div style={{display:'flex',gap:'0.5rem',marginBottom:'1rem'}}>
                          <button className={kelasTab==='mahasiswa'?'btn-primary':'btn-secondary'} style={{fontSize:'0.8rem',padding:'0.3rem 0.75rem'}} onClick={()=>setKelasTab('mahasiswa')}>
                            <Users size={14}/>Mahasiswa ({(kelasMahasiswa[k.id]||[]).length})
                          </button>
                          <button className={kelasTab==='mk'?'btn-primary':'btn-secondary'} style={{fontSize:'0.8rem',padding:'0.3rem 0.75rem'}} onClick={()=>setKelasTab('mk')}>
                            <BookMarked size={14}/>Mata Kuliah ({(kelasMataKuliah[k.id]||[]).length})
                          </button>
                        </div>

                        {/* Sub-tab: Mahasiswa */}
                        {kelasTab==='mahasiswa' && (
                          <div>
                            <div style={{fontWeight:600,fontSize:'0.85rem',marginBottom:'0.5rem',color:'#2D5B8C'}}>Mahasiswa di Kelas {k.nama}</div>
                            <div style={{display:'flex',flexWrap:'wrap',gap:'0.4rem',marginBottom:'0.75rem'}}>
                              {(kelasMahasiswa[k.id]||[]).length===0
                                ? <span style={{color:'#7C8990',fontSize:'0.8rem'}}>Belum ada mahasiswa</span>
                                : (kelasMahasiswa[k.id]||[]).map(m=>(
                                  <span key={m.km_id} style={{display:'inline-flex',alignItems:'center',gap:'0.3rem',background:'#E6ECF4',color:'#2D5B8C',borderRadius:'0.375rem',padding:'0.2rem 0.6rem',fontSize:'0.8rem'}}>
                                    {m.nim} — {m.name}
                                    <button onClick={()=>{
                                      showConfirm(`Hapus ${m.name} dari kelas?`, async()=>{
                                        setConfirmDialog(null);
                                        try{await api.removeMahasiswaFromKelas(m.km_id);await loadKelasMahasiswa(k.id);loadData();}catch(e){showToast(e.message,'error');}
                                      });
                                    }} style={{background:'none',border:'none',cursor:'pointer',color:'#2D5B8C',padding:0,display:'flex'}}><X size={12}/></button>
                                  </span>
                                ))
                              }
                            </div>
                            <div style={{display:'flex',gap:'0.5rem'}}>
                              <select value={addKmStudentId} onChange={e=>setAddKmStudentId(e.target.value)} className="period-input" style={{flex:1,maxWidth:'320px'}}>
                                <option value="">Tambah mahasiswa ke kelas...</option>
                                {students.filter(s=>!(kelasMahasiswa[k.id]||[]).find(m=>m.id===s.id)).map(s=>(
                                  <option key={s.id} value={s.id}>{s.nim} — {s.name}</option>
                                ))}
                              </select>
                              <button className="btn-primary" style={{padding:'0.4rem 0.75rem',fontSize:'0.8rem'}} onClick={async()=>{
                                if(!addKmStudentId)return;
                                try{await api.addMahasiswaToKelas(k.id,parseInt(addKmStudentId));setAddKmStudentId('');await loadKelasMahasiswa(k.id);loadData();}catch(e){showToast(e.message,'error');}
                              }}><Plus size={14}/>Tambah</button>
                            </div>
                          </div>
                        )}

                        {/* Sub-tab: Mata Kuliah */}
                        {kelasTab==='mk' && (
                          <div>
                            <div style={{fontWeight:600,fontSize:'0.85rem',marginBottom:'0.5rem',color:'#2D5B8C'}}>MK di Kelas {k.nama}</div>
                            <div style={{marginBottom:'0.75rem'}}>
                              {(kelasMataKuliah[k.id]||[]).length===0
                                ? <span style={{color:'#7C8990',fontSize:'0.8rem'}}>Belum ada MK yang diassign</span>
                                : (kelasMataKuliah[k.id]||[]).map(mk=>(
                                  <div key={mk.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'0.4rem 0.6rem',background:'white',borderRadius:'0.375rem',marginBottom:'0.4rem',border:'1px solid #C5D3E6',fontSize:'0.8rem'}}>
                                    <div>
                                      <span style={{fontWeight:600,color:'#2D5B8C'}}>{mk.course_code}</span>
                                      <span style={{color:'#3A474F'}}> — {mk.course_name}</span>
                                      <span style={{color:'#55636B',marginLeft:'0.5rem'}}>({mk.semester_nama})</span>
                                      <span style={{color:'#2F6E45',marginLeft:'0.5rem'}}>👤 {mk.dosen_name}</span>
                                    </div>
                                    <button onClick={()=>{
                                      showConfirm(`Hapus ${mk.course_code} dari kelas? Enrollment & pengampu ber-source kelas akan ikut terhapus.`, async()=>{
                                        setConfirmDialog(null);
                                        try{await api.removeMKFromKelas(mk.id);await loadKelasMataKuliah(k.id);loadData();}catch(e){showToast(e.message,'error');}
                                      });
                                    }} style={{background:'none',border:'none',cursor:'pointer',color:'#A63A3A',padding:'0.25rem'}}><X size={14}/></button>
                                  </div>
                                ))
                              }
                            </div>
                            {/* Assign MK form */}
                            <div style={{background:'white',padding:'0.75rem',borderRadius:'0.5rem',border:'1px dashed #A9BCD8'}}>
                              <div style={{fontSize:'0.8rem',fontWeight:600,color:'#3A474F',marginBottom:'0.5rem'}}>Assign MK baru ke kelas ini:</div>
                              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:'0.5rem',marginBottom:'0.5rem'}}>
                                <select value={addKmSemesterId} onChange={e=>setAddKmSemesterId(e.target.value)} className="period-input">
                                  <option value="">Pilih Semester...</option>
                                  {semesters.map(s=><option key={s.id} value={s.id}>{s.nama}</option>)}
                                </select>
                                <select value={addKmCourseId} onChange={e=>setAddKmCourseId(e.target.value)} className="period-input">
                                  <option value="">Pilih Mata Kuliah...</option>
                                  {courses.map(c=><option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}
                                </select>
                                <select value={addKmDosenId} onChange={e=>setAddKmDosenId(e.target.value)} className="period-input">
                                  <option value="">Pilih Dosen/CI...</option>
                                  {dosens.map(d=><option key={d.id} value={d.id}>{d.name} ({d.instructor_type})</option>)}
                                </select>
                              </div>
                              <button className="btn-primary" style={{padding:'0.4rem 0.75rem',fontSize:'0.8rem'}} onClick={async()=>{
                                if(!addKmSemesterId||!addKmCourseId||!addKmDosenId){showToast('Pilih semester, MK, dan dosen terlebih dahulu','warning');return;}
                                try{
                                  const res = await api.assignMKToKelas(k.id,{semesterId:parseInt(addKmSemesterId),courseId:parseInt(addKmCourseId),dosenId:parseInt(addKmDosenId)});
                                  showToast(res.message);
                                  setAddKmSemesterId('');setAddKmCourseId('');setAddKmDosenId('');
                                  await loadKelasMataKuliah(k.id);loadData();
                                }catch(e){showToast(e.message,'error');}
                              }}><Plus size={14}/>Assign MK ke Kelas</button>
                            </div>
                          </div>
                        )}
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
                {kelasList.length===0&&<tr><td colSpan={6} className="table-empty">Belum ada kelas</td></tr>}
              </tbody>
            </table></div></div>
          </div>
        )}

        {/* Analitik */}
        {activeTab === 'analitik' && (
          <AnalyticsDashboard user={user} />
        )}

        {/* Audit Log */}
        {activeTab === 'auditlog' && user.adminRole === 'super_admin' && (
          <AuditLogPage />
        )}

        {/* Admins */}
        {activeTab === 'admins' && user.adminRole === 'super_admin' && (
          <div className="card">
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:'1rem',alignItems:'center'}}>
              <h3 className="card-title" style={{margin:0}}><UserCog size={20}/>Manajemen Admin</h3>
              <button className="btn-primary" onClick={()=>{setShowForm(showForm==='admin'?null:'admin');setFormData({role:'co_admin'});setEditingId(null);}}>
                <Plus size={16}/>Tambah Admin
              </button>
            </div>
            {showForm === 'admin' && (
              <div style={{background:'#F5F7F6',padding:'1rem',borderRadius:'0.5rem',marginBottom:'1rem'}}>
                <div style={{fontWeight:600,fontSize:'0.9rem',marginBottom:'0.75rem',color:'#3A474F'}}>
                  {editingId==='me' ? 'Edit Profil Saya' : editingId ? 'Edit Admin' : 'Tambah Admin'}
                </div>
                <div className="period-form-grid">
                  <input placeholder="Nama" className="period-input" value={formData.name||''} onChange={e=>setFormData({...formData,name:e.target.value})}/>
                  <input placeholder="Email" type="email" className="period-input" value={formData.email||''} onChange={e=>setFormData({...formData,email:e.target.value})}/>
                  {editingId==='me' && (
                    <>
                      <input placeholder="Password lama (isi jika ingin ganti password)" type="password" className="period-input" value={formData.currentPassword||''} onChange={e=>setFormData({...formData,currentPassword:e.target.value})}/>
                      <input placeholder="Password baru (kosongkan jika tidak ingin ganti)" type="password" className="period-input" value={formData.password||''} onChange={e=>setFormData({...formData,password:e.target.value})}/>
                    </>
                  )}
                  {!editingId && <input placeholder="Password (opsional, kosongkan untuk password acak)" type="password" className="period-input" value={formData.password||''} onChange={e=>setFormData({...formData,password:e.target.value})}/>}
                  {editingId!=='me' && (
                    <select className="period-input" value={formData.role||'co_admin'} onChange={e=>setFormData({...formData,role:e.target.value})}>
                      <option value="co_admin">Co Admin</option>
                      <option value="super_admin">Super Admin</option>
                    </select>
                  )}
                </div>
                <div style={{display:'flex',gap:'0.5rem',marginTop:'1rem'}}>
                  <button className="btn-primary" onClick={()=>editingId==='me' ? handleUpdateOwnProfile() : editingId ? handleUpdateAdmin() : handleCreate('admin')}>Simpan</button>
                  <button className="btn-secondary" onClick={()=>{setShowForm(null);setFormData({});setEditingId(null);}}>Batal</button>
                </div>
              </div>
            )}
            <div className="table-container"><table className="data-table">
              <thead><tr><th>Nama</th><th>Email</th><th>Role</th><th>Aksi</th></tr></thead>
              <tbody>
                {admins.map(a=>(
                  <tr key={a.id} style={{background:a.id===user.id?'#F1F7F3':''}}>
                    <td>{a.name}{a.id===user.id&&<span style={{fontSize:'0.75rem',color:'#55636B',marginLeft:'0.4rem'}}>(Anda)</span>}</td>
                    <td>{a.email}</td>
                    <td><span className={`badge ${a.role==='super_admin'?'badge-info':'badge-warning'}`}>{a.role==='super_admin'?'Super Admin':'Co Admin'}</span></td>
                    <td style={{display:'flex',gap:'0.35rem'}}>
                      {a.id===user.id ? (
                        <button className="btn-secondary" style={{padding:'0.25rem 0.5rem'}} title="Edit Profil Saya"
                          onClick={()=>{setEditingId('me');setFormData({name:a.name,email:a.email,password:''});setShowForm('admin');}}>
                          <Edit size={14}/>
                        </button>
                      ) : (<>
                        <button className="btn-secondary" style={{padding:'0.25rem 0.5rem'}} title="Edit" onClick={()=>{setEditingId(a.id);setFormData({name:a.name,email:a.email,role:a.role});setShowForm('admin');}}>
                          <Edit size={14}/>
                        </button>
                        <button className="btn-secondary" style={{padding:'0.25rem 0.5rem'}} title="Reset Password" onClick={()=>handleResetAdminPassword(a.id,a.name)}>
                          <KeyRound size={14}/>
                        </button>
                        <button className="btn-danger" onClick={()=>handleDelete('admin',a.id)}>
                          <Trash2 size={14}/>
                        </button>
                      </>)}
                    </td>
                  </tr>
                ))}
                {admins.length===0&&<tr><td colSpan={4} className="table-empty">Belum ada admin lain</td></tr>}
              </tbody>
            </table></div>
          </div>
        )}

        {/* Assessments */}
        {activeTab === 'assessments' && (() => {
          const prodiList = [...new Set(students.map(s => s.program_studi))].filter(Boolean).sort();
          const { total, page, totalPages } = assessmentPagination;
          const applyFilters = (newFilters = exportFilters, newPage = 1) => {
            loadAssessments({ ...newFilters, search: searchTerm }, newPage);
          };
          return (
          <div className="card">
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:'1rem',flexWrap:'wrap',gap:'0.75rem',alignItems:'center'}}>
              <h3 className="card-title" style={{margin:0}}><FileText size={20}/>Hasil Penilaian <span style={{fontSize:'0.875rem',fontWeight:600,color:'#55636B'}}>({total} total)</span></h3>
              <button className="btn-success" onClick={exportAssessments}><Download size={14}/>Export Excel</button>
            </div>
            {/* Filters */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:'0.75rem',marginBottom:'1rem',padding:'1rem',background:'#F5F7F6',borderRadius:'0.5rem'}}>
              <select className="period-input" value={exportFilters.periodId} onChange={e=>setExportFilters(f=>({...f,periodId:e.target.value}))}>
                <option value="">Semua Periode</option>
                {periods.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <select className="period-input" value={exportFilters.programStudi} onChange={e=>setExportFilters(f=>({...f,programStudi:e.target.value}))}>
                <option value="">Semua Prodi</option>
                {prodiList.map(p=><option key={p} value={p}>{p}</option>)}
              </select>
              <select className="period-input" value={exportFilters.formType} onChange={e=>setExportFilters(f=>({...f,formType:e.target.value}))}>
                <option value="">Semua Tipe Form</option>
                <option value="kelas">Kelas</option>
                <option value="praktik">Praktik</option>
              </select>
              <select className="period-input" value={exportFilters.assessorRole} onChange={e=>setExportFilters(f=>({...f,assessorRole:e.target.value}))}>
                <option value="">Semua Penilai</option>
                <option value="dosen">Pengajar</option>
                <option value="mahasiswa">Mahasiswa</option>
              </select>
              <button className="btn-primary" onClick={()=>applyFilters()}><Search size={14}/>Terapkan</button>
              <button className="btn-secondary" onClick={()=>{const f={periodId:'',programStudi:'',formType:'',assessorRole:''};setExportFilters(f);setSearchTerm('');loadAssessments({},1);}}>Reset</button>
            </div>
            <div className="table-container"><div className="table-wrapper"><table className="data-table">
              <thead><tr><th>NIM</th><th>Nama</th><th>Prodi</th><th>MK</th><th>Periode</th><th>Tipe</th><th>Penilai</th><th>Skor</th><th>%</th><th>Level</th><th>Aksi</th></tr></thead>
              <tbody>
                {assessments.map(a=>(
                  <tr key={a.id}>
                    <td>{a.nim}</td><td>{a.student_name}</td><td>{a.program_studi}</td><td>{a.course_code}</td>
                    <td>{a.period_name}</td>
                    <td><span className="badge badge-info">{a.form_type}</span></td>
                    <td>{a.assessor_role==='dosen'
                      ? <span className="badge badge-info">{a.assessor_instructor_type==='CI'?'CI (Clinical Instructor)':'Dosen'}</span>
                      : <span className="badge badge-warning">Mahasiswa</span>
                    }</td>
                    <td>{a.total_score}/{a.max_score}</td>
                    <td>{a.percentage}%</td>
                    <td><span className="badge badge-success">Level {a.level}</span></td>
                    <td>
                      <button className="btn-danger" style={{padding:'0.25rem 0.5rem'}} title="Hapus" onClick={()=>showConfirm(
                        `Hapus penilaian ${a.student_name} (${a.course_code}, ${a.assessor_role})?`,
                        async()=>{setConfirmDialog(null);try{await api.deleteAssessment(a.id);showToast('Penilaian dihapus');loadAssessments(exportFilters,page);}catch(e){showToast(e.message,'error');}}
                      )}><Trash2 size={14}/></button>
                    </td>
                  </tr>
                ))}
                {assessments.length === 0 && <tr><td colSpan={11} className="table-empty">Tidak ada data</td></tr>}
              </tbody>
            </table></div></div>
            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:'1rem',flexWrap:'wrap',gap:'0.5rem'}}>
                <span style={{fontSize:'0.8rem',color:'#55636B'}}>Menampilkan {assessments.length} dari {total} data</span>
                <div style={{display:'flex',gap:'0.375rem',alignItems:'center'}}>
                  <button className="btn-secondary" style={{padding:'0.3rem 0.6rem'}} disabled={page===1} onClick={()=>loadAssessments({...exportFilters,search:searchTerm},page-1)}>‹ Prev</button>
                  {Array.from({length:Math.min(totalPages,7)},(_,i)=>{
                    let p; if(totalPages<=7){p=i+1;}else if(page<=4){p=i+1;}else if(page>=totalPages-3){p=totalPages-6+i;}else{p=page-3+i;}
                    return(<button key={p} className={p===page?'btn-primary':'btn-secondary'} style={{padding:'0.3rem 0.6rem',minWidth:'2rem'}} onClick={()=>loadAssessments({...exportFilters,search:searchTerm},p)}>{p}</button>);
                  })}
                  <button className="btn-secondary" style={{padding:'0.3rem 0.6rem'}} disabled={page===totalPages} onClick={()=>loadAssessments({...exportFilters,search:searchTerm},page+1)}>Next ›</button>
                </div>
              </div>
            )}
          </div>
          );
        })()}
      </div>
    </div>
  );
}

// ============================================
// ASSESSMENT FORM (DOSEN & MAHASISWA)
// ============================================
function DosenAnalyticsPage({ user, onLogout, onBack }) {
  return (
    <div className="main-content">
      <header className="app-header">
        <div className="header-container">
          <div className="header-left">
            <div className="header-logo">I-CARE</div>
            <div><h1 className="header-title">Analitik</h1><p className="header-subtitle">{user.name}</p></div>
          </div>
          <div style={{display:'flex',gap:'0.5rem'}}>
            <button onClick={onBack} className="btn-secondary">Kembali ke Penilaian</button>
            <button onClick={onLogout} className="btn-logout"><LogOut size={16}/><span>Logout</span></button>
          </div>
        </div>
      </header>
      <div className="content-container">
        <AnalyticsDashboard user={user} />
      </div>
    </div>
  );
}

function AssessmentForm({ user, onLogout, onOpenAnalytics, onOpenRiwayat, onOpenProfil }) {
  const [activePeriod, setActivePeriod] = useState(null);
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [prodiList, setProdiList] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [filterProdi, setFilterProdi] = useState('');
  const [formType, setFormType] = useState('kelas');
  const [selfAssessment, setSelfAssessment] = useState(null);
  const [dosenAssessment, setDosenAssessment] = useState(null);
  const [scores, setScores] = useState({integrity:{},compassion:{},assurance:{},respect:{},innovation:{}});
  const [expandedSections, setExpandedSections] = useState({integrity:true});
  const [showResult, setShowResult] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUnanswered, setShowUnanswered] = useState(false);
  const { toasts, showToast } = useToast();

  const formData = formType === 'kelas' ? formDataKelas : formDataPraktik;

  useEffect(() => { loadInitialData(); }, []);
  useEffect(() => { if (selectedCourse && user.role === 'dosen') loadStudents(); }, [selectedCourse, filterProdi]);
  useEffect(() => { if (selectedCourse) checkExistingAssessments(); }, [selectedCourse, selectedStudent, formType]);

  const loadInitialData = async () => {
    try {
      const periodRes = user.role === 'dosen' ? await api.getDosenActivePeriod() : await api.getMahasiswaActivePeriod();
      setActivePeriod(periodRes.data);
      const coursesRes = user.role === 'dosen' ? await api.getDosenCourses() : await api.getMahasiswaCourses();
      setCourses(coursesRes.data || []);
      if (user.role === 'dosen') {
        const prodiRes = await api.getProgramStudi();
        setProdiList(prodiRes.data || []);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const loadStudents = async () => {
    if (!selectedCourse) return;
    try {
      const res = await api.getCourseStudents(selectedCourse, filterProdi);
      setStudents(res.data || []);
    } catch (err) { console.error(err); }
  };

  const checkExistingAssessments = async () => {
    if (!selectedCourse || !activePeriod) return;
    try {
      if (user.role === 'mahasiswa') {
        const res = await api.checkMahasiswaAssessment(selectedCourse, formType, activePeriod.id);
        setSelfAssessment(res.selfAssessment);
        setDosenAssessment(res.dosenAssessment);
      } else if (selectedStudent) {
        const res = await api.checkDosenAssessment(selectedStudent, selectedCourse, formType, activePeriod.id);
        setDosenAssessment(res.exists ? res.data : null);
        setSelfAssessment(res.selfAssessment || null);
      }
    } catch (err) { console.error(err); }
  };

  const handleScoreChange = (section, index, value) => {
    setScores(prev => ({...prev, [section]: {...prev[section], [index]: parseInt(value)}}));
  };

  const calculateTotal = () => {
    let total = 0, max = 0;
    const sectionScores = {};
    Object.keys(formData).forEach(section => {
      const sectionTotal = Object.values(scores[section] || {}).reduce((a,b) => a+b, 0);
      const sectionMax = formData[section].maxScore;
      total += sectionTotal; max += sectionMax;
      sectionScores[section] = { total: sectionTotal, max: sectionMax, percentage: ((sectionTotal/sectionMax)*100).toFixed(1) };
    });
    return { total, max, percentage: ((total/max)*100).toFixed(1), sectionScores };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!activePeriod) { showToast('Tidak ada periode aktif!', 'warning'); return; }

    let allFilled = true;
    let firstUnanswered = null;
    Object.keys(formData).forEach(section => {
      formData[section].items.forEach((_, i) => {
        if (!scores[section]?.[i]) {
          allFilled = false;
          if (!firstUnanswered) firstUnanswered = { section, index: i };
        }
      });
    });
    if (!allFilled) {
      const unansweredCount = Object.keys(formData).reduce((total, section) =>
        total + formData[section].items.filter((_, i) => !scores[section]?.[i]).length, 0);

      setShowUnanswered(true);
      showToast(`Masih ada ${unansweredCount} pertanyaan yang belum dijawab`, 'warning');

      const sectionsToExpand = {};
      Object.keys(formData).forEach(section => {
        if (formData[section].items.some((_, i) => !scores[section]?.[i])) {
          sectionsToExpand[section] = true;
        }
      });
      setExpandedSections(prev => ({ ...prev, ...sectionsToExpand }));

      if (firstUnanswered) {
        setTimeout(() => {
          const el = document.getElementById(`item-${firstUnanswered.section}-${firstUnanswered.index}`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
      return;
    }

    const totals = calculateTotal();
    const levelInfo = getLevel(totals.percentage);
    const data = {
      courseId: parseInt(selectedCourse),
      periodId: activePeriod.id,
      formType, scores,
      sectionScores: totals.sectionScores,
      totalScore: totals.total,
      maxScore: totals.max,
      percentage: parseFloat(totals.percentage),
      level: levelInfo.level
    };
    if (user.role === 'dosen') data.studentId = parseInt(selectedStudent);

    try {
      if (user.role === 'dosen') await api.submitDosenAssessment(data);
      else await api.submitMahasiswaAssessment(data);
      setResultData({ ...data, levelInfo });
      setShowResult(true);
    } catch (err) { showToast(err.message, 'error'); }
  };

  const resetForm = () => {
    setShowResult(false); setResultData(null);
    setScores({integrity:{},compassion:{},assurance:{},respect:{},innovation:{}});
    setSelectedCourse(''); setSelectedStudent(''); setFormType('kelas');
    setSelfAssessment(null); setDosenAssessment(null);
    setShowUnanswered(false);
  };

  if (loading) return <div className="loading-screen"><div className="spinner"></div></div>;

  if (showResult) {
    return (
      <div className="main-content">
        <ToastContainer toasts={toasts}/>
        <header className="app-header">
          <div className="header-container">
            <div className="header-left">
              <div className="header-logo">I-CARE</div>
              <div><h1 className="header-title">Penilaian tersimpan</h1></div>
            </div>
            <button onClick={onLogout} className="btn-logout"><LogOut size={16}/></button>
          </div>
        </header>
        <div className="content-container">
          <div className="message-box success">
            <CheckCircle2 size={32}/>
            <h3>Penilaian tersimpan</h3>
            <p>Skor: {resultData.totalScore}/{resultData.maxScore} ({resultData.percentage}%)</p>
            <p>Level: {resultData.levelInfo.label} - {resultData.levelInfo.desc}</p>
          </div>
          <button onClick={resetForm} className="btn-primary" style={{marginTop:'2rem'}}><Plus size={20}/>Isi Penilaian Baru</button>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <ToastContainer toasts={toasts}/>
      <header className="app-header">
        <div className="header-container">
          <div className="header-left">
            <div className="header-logo">I-CARE</div>
            <div><h1 className="header-title">Formulir I-CARE</h1><p className="header-subtitle">{user.name}</p></div>
          </div>
          <div style={{display:'flex',gap:'0.5rem'}}>
            <NotificationBell user={user} />
            {onOpenAnalytics && (
              <button onClick={onOpenAnalytics} className="btn-secondary"><TrendingUp size={16}/><span>Analitik</span></button>
            )}
            {onOpenRiwayat && (
              <button onClick={onOpenRiwayat} className="btn-secondary"><History size={16}/><span>Riwayat Nilai</span></button>
            )}
            {onOpenProfil && (
              <button onClick={onOpenProfil} className="btn-secondary"><UserCog size={16}/><span>Profil Saya</span></button>
            )}
            <button onClick={onLogout} className="btn-logout"><LogOut size={16}/><span>Logout</span></button>
          </div>
        </div>
      </header>

      <div className="content-container">
        {!activePeriod && <div className="alert alert-warning">Tidak ada periode aktif.</div>}

        {/* Dosen: Filter Prodi */}
        {user.role === 'dosen' && (
          <div className="card">
            <label className="form-label">Filter Program Studi</label>
            <select className="period-input" value={filterProdi} onChange={e=>{setFilterProdi(e.target.value);setSelectedStudent('');}}>
              <option value="">Semua Prodi</option>
              {prodiList.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        )}

        {/* Select Course */}
        <div className="card">
          <label className="form-label">Pilih Mata Kuliah</label>
          <select className="period-input" value={selectedCourse} onChange={e=>{setSelectedCourse(e.target.value);setSelectedStudent('');}}>
            <option value="">-- Pilih MK --</option>
            {courses.map(c => <option key={c.id} value={c.id}>{c.code} - {c.name}</option>)}
          </select>
        </div>

        {/* Dosen: Select Student */}
        {user.role === 'dosen' && selectedCourse && (
          <div className="card">
            <label className="form-label">Pilih Mahasiswa</label>
            <select className="period-input" value={selectedStudent} onChange={e=>setSelectedStudent(e.target.value)}>
              <option value="">-- Pilih Mahasiswa --</option>
              {students.map(s => <option key={s.id} value={s.id}>{s.nim} - {s.name}</option>)}
            </select>
          </div>
        )}

        {/* Form Type */}
        {selectedCourse && (user.role === 'mahasiswa' || selectedStudent) && (
          <div className="card">
            <label className="form-label">Tipe Formulir</label>
            <div className="form-type-buttons">
              <button onClick={()=>setFormType('kelas')} className={`btn-form-type ${formType==='kelas'?'active':'inactive'}`}>Kelas</button>
              <button onClick={()=>setFormType('praktik')} className={`btn-form-type ${formType==='praktik'?'active':'inactive'}`}>Praktik</button>
            </div>
          </div>
        )}

        {/* Status */}
        {selectedCourse && (user.role === 'mahasiswa' || selectedStudent) && (
          <div className="card">
            <h3 className="card-title">Status Penilaian</h3>
            <div className="status-grid">
              <div className={`status-item ${selfAssessment?'completed':'pending'}`}>
                {selfAssessment?<CheckCircle2 size={18}/>:<Clock size={18}/>}
                <div>
                  <div>Self Assessment: {selfAssessment?'Sudah':'Belum'}</div>
                  {selfAssessment && <div style={{fontSize:'0.8rem',color:'#3A474F',marginTop:'0.2rem'}}>
                    Skor: {selfAssessment.total_score}/{selfAssessment.max_score} ({selfAssessment.percentage}%) — Level {selfAssessment.level}
                  </div>}
                </div>
              </div>
              <div className={`status-item ${dosenAssessment?'completed':'pending'}`}>
                {dosenAssessment?<CheckCircle2 size={18}/>:<Clock size={18}/>}
                <div>
                  <div>Penilaian Pengajar: {dosenAssessment?'Sudah':'Belum'}</div>
                  {dosenAssessment && (user.role !== 'mahasiswa' || selfAssessment) && (
                    <div style={{fontSize:'0.8rem',color:'#3A474F',marginTop:'0.2rem'}}>
                      Skor: {dosenAssessment.total_score}/{dosenAssessment.max_score} ({dosenAssessment.percentage}%) — Level {dosenAssessment.level}
                    </div>
                  )}
                  {dosenAssessment && user.role === 'mahasiswa' && !selfAssessment && (
                    <div style={{fontSize:'0.8rem',color:'#7C8990',marginTop:'0.2rem'}}>
                      Isi self assessment terlebih dahulu untuk melihat nilai
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Show message if already assessed */}
        {user.role === 'mahasiswa' && selfAssessment && (
          <div className="message-box success"><CheckCircle2 size={28}/><h3>Self assessment untuk mata kuliah ini sudah diisi.</h3></div>
        )}
        {user.role === 'dosen' && dosenAssessment && (
          <div className="message-box success"><CheckCircle2 size={28}/><h3>Penilaian untuk mahasiswa ini sudah diisi.</h3></div>
        )}

        {/* Form */}
        {selectedCourse && (user.role === 'mahasiswa' || selectedStudent) && 
         !((user.role === 'mahasiswa' && selfAssessment) || (user.role === 'dosen' && dosenAssessment)) && activePeriod && (
          <form onSubmit={handleSubmit}>
            {Object.keys(formData).map(sectionKey => {
              const section = formData[sectionKey];
              const isExpanded = expandedSections[sectionKey];
              return (
                <div key={sectionKey} className="section-card" style={{borderLeftColor:section.color}}>
                  <button type="button" onClick={()=>setExpandedSections(prev=>({...prev,[sectionKey]:!prev[sectionKey]}))} className="section-header">
                    <div className="section-header-left">
                      <span className="section-letter" style={{background:section.color}} aria-hidden="true">{section.title[0]}</span>
                      <div><h3 className="section-title-text">{section.title}</h3><p className="section-subtitle">{section.subtitle}</p></div>
                    </div>
                    {isExpanded ? <ChevronUp size={24}/> : <ChevronDown size={24}/>}
                  </button>
                  {isExpanded && (
                    <div className="section-body">
                      {section.items.map((item, idx) => (
                        <div key={idx} id={`item-${sectionKey}-${idx}`} className="assessment-item"
                          style={showUnanswered && !scores[sectionKey]?.[idx] ? {borderColor:'#A63A3A',background:'#FBF1F0'} : {}}>
                          <div className="item-header">
                            <div className="item-number">{idx+1}</div>
                            <p className="item-text">{item}</p>
                          </div>
                          <div className="radio-options">
                            {[{v:4,l:'Selalu'},{v:3,l:'Sering'},{v:2,l:'Kadang'},{v:1,l:'Jarang'}].map(opt => {
                              const isActive = scores[sectionKey]?.[idx] === opt.v;
                              return (
                                <label key={opt.v} className={`radio-label ${isActive?'active':''}`}>
                                  <input type="radio" name={`${sectionKey}-${idx}`} value={opt.v} onChange={e=>handleScoreChange(sectionKey,idx,e.target.value)} className="radio-input" required/>
                                  <div className="radio-value">{opt.v}</div><div className="radio-text">{opt.l}</div>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <div className="submit-container">
              <button type="submit" className="btn-submit"><CheckCircle2 size={20}/>Kirim penilaian</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default App;
